import React from "react";
import { Animated, useWindowDimensions } from "react-native";
import { useLocation, useNavigate } from "react-router-dom";

import { SessionContext } from "../../../contexts";
import {
  buildSigninPathWithReturnTo,
  clearPendingPostAuthRedirectPath,
  getCurrentRelativeLocation,
  getPendingPostAuthRedirectPath,
  setPendingPostAuthRedirectPath,
} from "../../../utils/authRedirect";
import { supabase } from "../../../utils/supabase";
import { THomeTab, TSession } from "../../../utils/types";
import { HOME_MOBILE_BREAKPOINT } from "./layout.constants";

export const DESKTOPTABSIZESMALL = 65;
export const TABSIZE = 200;

type THomeLayoutController = {
  width: number;
  height: number;
  isMobile: boolean;
  session: TSession | null;
  isSessionLoading: boolean;
  isModalOpen: boolean;
  isMobileMenuOpen: boolean;
  isMinimized: boolean;
  animatedHeight: Animated.Value;
  animatedTabSize: Animated.Value;
  spinValue: Animated.Value;
  tabs: THomeTab[];
  handleToggleHamburger: () => void;
  handleSignOut: () => Promise<void>;
  toggleDesktopSidebar: () => void;
  closeClinicInfoModal: () => void;
};

const useHomeLayoutController = (): THomeLayoutController => {
  const { width, height } = useWindowDimensions();
  const isMobile = Math.min(width, height) < HOME_MOBILE_BREAKPOINT;
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const session = React.useContext(SessionContext)?.session ?? null;

  const animatedTabSize = React.useRef(new Animated.Value(TABSIZE)).current;
  const spinValue = React.useRef(new Animated.Value(0)).current;
  const animatedHeight = React.useRef(new Animated.Value(0)).current;

  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [isMinimized, setIsMinimized] = React.useState<boolean>(false);
  const [isSessionLoading, setIsSessionLoading] = React.useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  React.useLayoutEffect(() => {
    const navigateToSigninOrDefaultPage = async () => {
      setIsSessionLoading(true);

      if (!session) {
        try {
          const { data, error } = await supabase.auth.getUser();
          const { user: currentUser } = data;

          if (error || !currentUser) {
            setPendingPostAuthRedirectPath(getCurrentRelativeLocation());
            navigate(
              buildSigninPathWithReturnTo(getCurrentRelativeLocation()),
              { replace: true },
            );
            return;
          }

          if (routerLocation.pathname !== "/") return;
          navigate("/home");
        } catch (error) {
          console.error("Error checking authentication:", error);
          setPendingPostAuthRedirectPath(getCurrentRelativeLocation());
          navigate(buildSigninPathWithReturnTo(getCurrentRelativeLocation()), {
            replace: true,
          });
        } finally {
          setIsSessionLoading(false);
        }
      } else {
        setIsSessionLoading(false);
      }
    };

    navigateToSigninOrDefaultPage();
  }, [navigate, routerLocation.pathname, session]);

  React.useEffect(() => {
    if (!session) return;

    const pendingReturnTo = getPendingPostAuthRedirectPath();
    if (!pendingReturnTo) return;

    const currentPath = `${routerLocation.pathname}${routerLocation.search}${routerLocation.hash}`;
    if (pendingReturnTo === currentPath) {
      clearPendingPostAuthRedirectPath();
      return;
    }

    clearPendingPostAuthRedirectPath();
    navigate(pendingReturnTo, { replace: true });
  }, [
    navigate,
    routerLocation.hash,
    routerLocation.pathname,
    routerLocation.search,
    session,
  ]);

  React.useEffect(() => {
    if (session && session.role === "PATIENT") navigate("/patient-app");
    if (session && !session.clinicId) setIsModalOpen(true);
  }, [navigate, session]);

  const handleToggleHamburger = React.useCallback(() => {
    setIsMobileMenuOpen((currentValue) => {
      const nextValue = !currentValue;
      Animated.timing(animatedHeight, {
        toValue: nextValue ? height - 55 : 0,
        duration: 300,
        useNativeDriver: false,
      }).start();
      return nextValue;
    });
  }, [animatedHeight, height]);

  const toggleDesktopSidebar = React.useCallback(() => {
    const nextIsMinimized = !isMinimized;

    setIsMinimized(nextIsMinimized);
    Animated.timing(animatedTabSize, {
      toValue: nextIsMinimized ? DESKTOPTABSIZESMALL : TABSIZE,
      duration: 300,
      useNativeDriver: true,
    }).start();
    Animated.timing(spinValue, {
      toValue: nextIsMinimized ? 1 : 0,
      duration: 300,
      useNativeDriver: true,
    }).start();
  }, [animatedTabSize, isMinimized, spinValue]);

  const handleSignOut = React.useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut({ scope: "global" });
      if (error) {
        if (error.message?.includes("session_not_found")) {
          // Session already invalid, clear local state
          await supabase.auth.signOut({ scope: "local" });
        } else {
          throw error;
        }
      }
      navigate("/signin");
    } catch (error) {
      // Fallback: clear local state and navigate anyway
      await supabase.auth.signOut({ scope: "local" });
      navigate("/signin");
    }
  }, [navigate]);

  const closeClinicInfoModal = React.useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const tabs = React.useMemo<THomeTab[]>(() => {
    const baseTabs: THomeTab[] = [
      "Simulate" as THomeTab,
      "Before-Afters" as THomeTab,
      "Gallery" as THomeTab,
      "Image Tools" as THomeTab,
      "Patients" as THomeTab,
      ...(session?.clinicId !== "22251c06-8786-4845-92cc-d781e4b35ea6"
        ? (["Consults"] as THomeTab[])
        : []),
      // "Messages" as THomeTab,
      // "Conversations" as THomeTab,
      // ^ To release calling: un-hide this AND set EXPO_PUBLIC_COMMUNICATIONS_ENABLED,
      // which gates incoming-call registration in CallSessionProvider.
      // ...(process.env.EXPO_PUBLIC_ENV !== "production"
      //   ? (["Appointments"] as THomeTab[])
      //   : []),
      "Analytics" as THomeTab,
      "Deploy Lead Gen" as THomeTab,
      "Upgrade" as THomeTab,
    ];

    const tabsForGoal =
      session?.primaryGoal === "lead_capture"
        ? baseTabs.filter(
            (tab) =>
              tab !== "Before-Afters" &&
              tab !== "Gallery" &&
              tab !== "Image Tools" &&
              tab !== "Consults",
          )
        : baseTabs;

    const shouldPinDeployLeadGen =
      session?.primaryGoal === "lead_capture" && !session?.deployStatus;

    const orderedTabs = shouldPinDeployLeadGen
      ? [
          "Deploy Lead Gen" as THomeTab,
          ...tabsForGoal.filter((tab) => tab !== "Deploy Lead Gen"),
        ]
      : tabsForGoal;

    if (session?.planType !== "FREE") {
      return orderedTabs;
    }

    return [
      "Upgrade" as THomeTab,
      ...orderedTabs.filter((tab) => tab !== "Upgrade"),
    ];
  }, [
    session?.clinicId,
    session?.deployStatus,
    session?.planType,
    session?.primaryGoal,
  ]);

  return {
    width,
    height,
    isMobile,
    session,
    isSessionLoading,
    isModalOpen,
    isMobileMenuOpen,
    isMinimized,
    animatedHeight,
    animatedTabSize,
    spinValue,
    tabs,
    handleToggleHamburger,
    handleSignOut,
    toggleDesktopSidebar,
    closeClinicInfoModal,
  };
};

export default useHomeLayoutController;
